import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout, { refreshAdminNotifs } from '../../components/TrackerLayout';
import { useAdminSession } from '../../context/TrackerContext';
import { getPaymentDateRequests, reviewPaymentDateRequest, setPaymentDueDate, DATED_PAYMENT_STATUSES } from '../../lib/api';
import type { PaymentDateRequest, PaymentDateStatus } from '../../lib/api';

const rupees  = (n: number) => '₹' + Number(n || 0).toLocaleString('en-IN');
const fmtDay  = (d: string) => d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtWhen = (iso: string | null) => iso
  ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
  : '';

type Filter = 'Pending' | 'Due today' | 'Overdue' | 'Upcoming' | 'Asked' | 'Closed';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'Pending',   label: '🙋 Waiting for you' },
  { key: 'Due today', label: '📅 Due today' },
  { key: 'Overdue',   label: '⏰ Overdue' },
  { key: 'Upcoming',  label: '🗓️ Upcoming' },
  { key: 'Asked',     label: '⌛ Waiting for student' },
  { key: 'Closed',    label: '✓ Closed' },
];

const STATUS_STYLE: Record<PaymentDateStatus, { bg: string; color: string; label: string }> = {
  Asked:     { bg: '#f3f4f6', color: '#6b7280', label: 'Waiting for student' },
  Pending:   { bg: '#fef3c7', color: '#92400e', label: 'Waiting for you' },
  Approved:  { bg: '#dcfce7', color: '#15803d', label: 'Approved' },
  Rejected:  { bg: '#fee2e2', color: '#b91c1c', label: 'Not approved — new date given' },
  Scheduled: { bg: '#dbeafe', color: '#1d4ed8', label: 'Date set by admin' },
};

function bucket(r: PaymentDateRequest, today: string): Filter {
  if (!r.open) return 'Closed';
  if (r.status === 'Pending') return 'Pending';
  if (r.status === 'Asked') return 'Asked';
  if (r.dueDate === today) return 'Due today';
  return r.dueDate < today ? 'Overdue' : 'Upcoming';
}

// ── Main Page ─────────────────────────────────────────────────────────────────
// Students who still owe part of their fee pick their next payment date once
// they've attended 5 classes. Admin approves it, or rejects it and gives a
// date; the student is emailed either way. A request closes by itself once a
// payment is recorded after it, and a student who still owes is asked again.

const PaymentDates: React.FC = () => {
  const { adminPassword } = useAdminSession();
  const [requests, setRequests] = useState<PaymentDateRequest[]>([]);
  const [today, setToday]       = useState('');
  const [filter, setFilter]     = useState<Filter>('Pending');
  const [loading, setLoading]   = useState(true);
  const [busy, setBusy]         = useState<string | null>(null);
  // The one card whose reject / set-date form is open
  const [panel, setPanel] = useState<{ id: string; mode: 'reject' | 'date' } | null>(null);
  const [date, setDate]   = useState('');
  const [notes, setNotes] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPaymentDateRequests(adminPassword);
      setRequests(res.requests ?? []);
      setToday(res.today);
    } catch (err: any) { toast.error('Failed to load: ' + err.message); }
    finally { setLoading(false); }
  }, [adminPassword]);

  useEffect(() => { load(); }, [load]);

  const counts = FILTERS.reduce((acc, f) => ({ ...acc, [f.key]: 0 }), {} as Record<Filter, number>);
  requests.forEach(r => { counts[bucket(r, today)]++; });

  const visible = requests
    .filter(r => bucket(r, today) === filter)
    .sort((a, b) =>
      filter === 'Pending' ? (a.requestedAt || '').localeCompare(b.requestedAt || '')
      : filter === 'Asked' ? a.createdAt.localeCompare(b.createdAt)
      : filter === 'Closed' ? (b.settledAt || '').localeCompare(a.settledAt || '')
      : a.dueDate.localeCompare(b.dueDate));

  const openPanel = (r: PaymentDateRequest, mode: 'reject' | 'date') => {
    setPanel({ id: r._id, mode });
    setDate(mode === 'date' && r.dueDate >= today ? r.dueDate : '');
  };

  const done = (r: PaymentDateRequest, message: string, emailSentTo: string) => {
    toast.success(message);
    if (!emailSentTo) toast.info(`${r.studentName} has no email saved, so they weren't emailed — tell them by phone`);
    setPanel(null);
    load();
    refreshAdminNotifs();
  };

  const approve = async (r: PaymentDateRequest) => {
    setBusy(r._id);
    try {
      const res = await reviewPaymentDateRequest(adminPassword, r._id, 'Approved', '', notes[r._id] ?? '');
      done(r, `Approved — ${r.studentName} will pay on ${fmtDay(r.requestedDate)}`, res.emailSentTo);
    } catch (err: any) { toast.error(err.message); }
    finally { setBusy(null); }
  };

  const submitPanel = async (r: PaymentDateRequest) => {
    if (!panel || !date) { toast.error('Pick a date first'); return; }
    setBusy(r._id);
    try {
      const note = notes[r._id] ?? '';
      if (panel.mode === 'reject') {
        const res = await reviewPaymentDateRequest(adminPassword, r._id, 'Rejected', date, note);
        done(r, `Not approved — ${r.studentName} has time till ${fmtDay(date)}`, res.emailSentTo);
      } else {
        const res = await setPaymentDueDate(adminPassword, r._id, date, note);
        done(r, `${r.studentName}'s payment date is now ${fmtDay(date)}`, res.emailSentTo);
      }
    } catch (err: any) { toast.error(err.message); }
    finally { setBusy(null); }
  };

  return (
    <TrackerLayout>
      <div className="space-y-5">
        <div className="rounded-2xl overflow-hidden shadow-lg"
          style={{ background: 'linear-gradient(135deg,#2b2240 0%,#3d2f5f 50%,#2b2240 100%)' }}>
          <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">💳</span>
                <h1 className="text-xl font-bold text-white">Payment Dates</h1>
              </div>
              <p className="text-white/50 text-xs max-w-2xl">
                Students who still owe fees are asked for their next payment date after 5 classes. Approve it, or reject it and give
                a date — they're emailed either way. A date closes by itself once a payment is recorded on the Payments page.
              </p>
            </div>
            <button onClick={load}
              className="self-start sm:self-auto px-3 py-2 rounded-xl text-xs font-medium border border-white/20 text-white/70 hover:bg-white/10 transition-colors">
              ↻ Refresh
            </button>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          {FILTERS.map(f => (
            <button key={f.key} onClick={() => { setFilter(f.key); setPanel(null); }}
              className="text-xs font-semibold px-4 py-2 rounded-xl border-2 transition-colors"
              style={filter === f.key
                ? { background: f.key === 'Overdue' ? '#b91c1c' : '#2b2240', color: '#fff', borderColor: f.key === 'Overdue' ? '#b91c1c' : '#2b2240' }
                : { background: '#fff', color: '#6b7280', borderColor: '#e5e7eb' }}>
              {f.label}{counts[f.key] > 0 && f.key !== 'Closed' ? ` (${counts[f.key]})` : ''}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <div className="w-8 h-8 rounded-full border-purple-200 border-t-purple-600 animate-spin" style={{ borderWidth: 3 }} />
            <p className="text-sm text-gray-400">Loading payment dates…</p>
          </div>
        ) : visible.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-14 text-center">
            <div className="text-5xl mb-3">✅</div>
            <p className="text-gray-700 font-bold text-base">
              {filter === 'Pending' ? 'No payment dates waiting for approval'
                : filter === 'Due today' ? 'No payments due today'
                : filter === 'Overdue' ? 'No overdue payments'
                : 'Nothing here'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {visible.map(r => {
              const st = STATUS_STYLE[r.status];
              const b = bucket(r, today);
              const isBusy = busy === r._id;
              const dated = DATED_PAYMENT_STATUSES.includes(r.status);
              const panelOpen = panel?.id === r._id;
              return (
                <div key={r._id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                  style={b === 'Pending' ? { borderLeftWidth: 4, borderLeftColor: '#F0B429' }
                    : b === 'Overdue' ? { borderLeftWidth: 4, borderLeftColor: '#dc2626' }
                    : b === 'Due today' ? { borderLeftWidth: 4, borderLeftColor: '#16a34a' } : {}}>
                  <div className="p-5 space-y-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-gray-800">{r.studentName}</p>
                          <span className="text-xs text-gray-400">({r.studentId})</span>
                          <span className="text-[11px] px-2 py-0.5 rounded-md font-semibold" style={{ background: '#ede9fe', color: '#5b21b6' }}>{r.course}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {[r.phone, r.email].filter(Boolean).join(' · ') || 'No phone or email on file'}
                        </p>
                      </div>
                      <span className="text-xs font-bold px-2.5 py-1 rounded-lg" style={{ background: st.bg, color: st.color }}>
                        {r.open ? st.label : 'Closed'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[
                        { label: 'Balance now', value: rupees(r.open ? r.currentBalance : r.balance), color: '#dc2626' },
                        { label: 'Paid', value: `${rupees(r.open ? r.currentTotalPaid : r.totalPaid)} / ${rupees(r.courseFee)}`, color: '#2b2240' },
                        { label: 'Classes when asked', value: String(r.classesAttended), color: '#2b2240' },
                        dated
                          ? { label: 'Pay by', value: fmtDay(r.dueDate), color: b === 'Overdue' ? '#dc2626' : '#15803d' }
                          : r.status === 'Pending'
                            ? { label: 'Asked to pay on', value: fmtDay(r.requestedDate), color: '#92400e' }
                            : { label: 'Asked', value: fmtWhen(r.createdAt), color: '#6b7280' },
                      ].map(c => (
                        <div key={c.label} className="rounded-xl px-3 py-2" style={{ background: '#f8f6ff' }}>
                          <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{c.label}</p>
                          <p className="text-sm font-bold" style={{ color: c.color }}>{c.value}</p>
                        </div>
                      ))}
                    </div>

                    {r.status === 'Rejected' && (
                      <p className="text-xs text-gray-500">The student asked for {fmtDay(r.requestedDate)} — not approved.</p>
                    )}
                    {r.studentNote && (
                      <div className="rounded-xl px-3 py-2 text-sm bg-gray-50 text-gray-700">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wide mr-1.5">Student:</span>{r.studentNote}
                      </div>
                    )}
                    {r.adminNote && dated && (
                      <p className="text-xs text-gray-400">Your note: <span className="text-gray-600">“{r.adminNote}”</span></p>
                    )}
                    {r.status === 'Asked' && r.open && (
                      <p className="text-xs text-gray-400">
                        {r.email ? `Emailed ${fmtWhen(r.askEmailSentAt || r.createdAt)} — hasn't picked a date yet.` : 'No email on file — call them and set the date here.'}
                      </p>
                    )}
                    {!r.open && (
                      <p className="text-xs text-gray-400">{r.settledReason} · {fmtWhen(r.settledAt)}</p>
                    )}

                    {/* Actions */}
                    {r.open && panelOpen && (
                      <div className="rounded-xl border-2 p-3 space-y-2" style={{ borderColor: panel!.mode === 'reject' ? '#fecaca' : '#c7d2fe' }}>
                        <p className="text-xs font-bold" style={{ color: panel!.mode === 'reject' ? '#b91c1c' : '#3730a3' }}>
                          {panel!.mode === 'reject' ? `Give ${r.studentName.split(' ')[0]} time till:` : 'New payment date:'}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input type="date" value={date} min={today} onChange={e => setDate(e.target.value)}
                            className="px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400" />
                          <input value={notes[r._id] ?? ''} onChange={e => setNotes(n => ({ ...n, [r._id]: e.target.value }))}
                            placeholder={`Note to ${r.studentName.split(' ')[0]} (optional)`}
                            className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400" />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => setPanel(null)} disabled={isBusy}
                            className="px-4 py-2 rounded-xl text-xs font-bold border-2 border-gray-200 text-gray-500">Cancel</button>
                          <button onClick={() => submitPanel(r)} disabled={isBusy || !date}
                            className="px-4 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-50"
                            style={{ background: panel!.mode === 'reject' ? '#dc2626' : '#2b2240' }}>
                            {isBusy ? '…' : panel!.mode === 'reject' ? '✕ Reject & give this date' : 'Save date'}
                          </button>
                        </div>
                      </div>
                    )}
                    {r.open && !panelOpen && (
                      r.status === 'Pending' ? (
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input value={notes[r._id] ?? ''} onChange={e => setNotes(n => ({ ...n, [r._id]: e.target.value }))}
                            placeholder={`Note to ${r.studentName.split(' ')[0]} (optional)`}
                            className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400" />
                          <div className="flex gap-2">
                            <button onClick={() => openPanel(r, 'reject')} disabled={isBusy}
                              className="px-4 py-2 rounded-xl text-xs font-bold border-2 disabled:opacity-50"
                              style={{ borderColor: '#ef4444', color: '#b91c1c' }}>
                              ✕ Reject…
                            </button>
                            <button onClick={() => approve(r)} disabled={isBusy || r.requestedDate < today}
                              title={r.requestedDate < today ? 'That date has passed — reject it and give a new date' : undefined}
                              className="px-4 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-50"
                              style={{ background: '#16a34a' }}>
                              {isBusy ? '…' : '✓ Approve'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-end">
                          <button onClick={() => openPanel(r, 'date')} disabled={isBusy}
                            className="px-4 py-2 rounded-xl text-xs font-bold border-2 disabled:opacity-50"
                            style={{ borderColor: '#c7d2fe', color: '#3730a3' }}>
                            📅 {r.status === 'Asked' ? 'Set date' : 'Change date'}
                          </button>
                        </div>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </TrackerLayout>
  );
};

export default PaymentDates;
